/**
 * API client for the deploy-export Edge Function.
 * Handles upload token generation, deployment, listing, and deletion
 * of published HTML exports via Vercel Blob + Vercel Deployments.
 */

import { requireAuth, invokeEdgeFn } from './supabaseClient.ts';

// ── Types ────────────────────────────────────────────────────────────

export interface UploadToken {
  clientToken: string;
  exportId: string;
  pathPrefix: string;
}

export interface DeployResult {
  deployment_url: string;
  export_id: string;
}

export interface PublishedExport {
  id: string;
  title: string;
  deployment_url: string;
  export_id: string;
  created_at: string;
  total_size_bytes: number;
  shot_count: number;
  scene_count: number;
  duration_seconds: number;
}

export interface DeployStats {
  totalBytes: number;
  shotCount: number;
  sceneCount: number;
  durationSeconds: number;
}

// ── Public API ───────────────────────────────────────────────────────

export async function getUploadToken(): Promise<UploadToken> {
  await requireAuth();
  const data = await invokeEdgeFn('deploy-export', { action: 'get-upload-token' });
  return data as UploadToken;
}

export async function deployHtml(
  html: string,
  title: string,
  blobUrls: string[],
  exportId: string,
  stats: DeployStats,
): Promise<DeployResult> {
  await requireAuth();
  const data = await invokeEdgeFn('deploy-export', {
    action: 'deploy',
    html,
    title,
    blob_urls: blobUrls,
    export_id: exportId,
    stats,
  });
  return data as DeployResult;
}

export async function listExports(): Promise<PublishedExport[]> {
  await requireAuth();
  const data = await invokeEdgeFn('deploy-export', { action: 'list-exports' });
  return (data.exports ?? []) as PublishedExport[];
}

export async function deleteExport(exportId: string): Promise<void> {
  await requireAuth();
  await invokeEdgeFn('deploy-export', { action: 'delete-export', export_id: exportId });
}
